/**
 * Does this column name denote dollars?
 *
 * Table cells pick their format by column NAME (tablePresentation.ts passes no
 * label), so this predicate is the only thing standing between a dollar column
 * and a bare number. It lives in .mjs, not format.ts, so `node --test` can
 * execute it against every column in the schema rather than grep its source.
 *
 * The defect that prompted extracting it: `top_ar_accounts` names its AR aging
 * buckets WITHOUT the `ar_` prefix that the MER-438 rule expects, so the
 * Collections Queue rendered "Over 60" as a bare `37,954.14` in the same row as
 * a correctly formatted `$145,204`. The same gap left `revenue_per_stop`
 * rendering as `18.47` on the Operations tab for both tenants: the historical
 * rule is anchored to the END of the name, and that column ends in `_stop`.
 */

/**
 * Money words that mean dollars WHEREVER they appear in the name, so
 * `revenue_per_stop` and `cost_per_mile` are caught, not just names that end in
 * a money word.
 *
 * "total" is deliberately absent: it prefixes plenty of non-money columns
 * (total_labor_hours, total_miles, total_stops). It stays in the anchored
 * end-of-name rule below, where it is safe.
 */
const MONEY_WORD =
  /(_|^)(revenue|costs?|margin|cash|balance|fees?|charge[ds]?|paid|collected|earned|billed|amount|spend|invoiced|price|mrr|emr|credits?|surcharge|taxes?|payments?)(_|$)/;

/**
 * A money word in the name does NOT make the value money when the name ends
 * this way. Without it, `revenue_recognition_date`, `billed_month` and
 * `margin_flag` all read as dollars. Two pre-existing false positives fall out
 * too: `ar_type` and `ar_period` are text, and the `/^ar_/` rule below was
 * formatting them as currency.
 */
const NON_MONEY_SUFFIX =
  /(_|^)(id|uuid|dates?|status|type|name|label|flag|code|notes?|reason|method|periods?|bucket|start|end|month|year|week|quarter|counts?|qty|score|rank|pct|percent|ratio|tier|class|group|segment|rep|manager|category|accounts?|terms|direction|clean|hold|cy|lbs|yoy|growth)$/;

/**
 * Prefixes that make a column a boolean or a tally no matter what money word
 * follows: `is_credit_memo` is a flag, `accounts_credit_hold` is a bigint
 * count, `distinct_billed_periods` is a tally.
 */
const NON_MONEY_PREFIX = /^(is|has|had|any|distinct|accounts|num|n)_/;

/** The four unprefixed AR aging buckets on `top_ar_accounts`. */
const AR_BUCKET = /^(current|past_\d+|over_\d+)$/;

/** Names that END in a money word. The original MER-176-era rule. */
const MONEY_TAIL =
  /(_|^)(revenue|cost|margin|cash|balance|fees?|charge[ds]?|paid|collected|earned|billed|disposal_cost|amount|ar|emr|spend|invoice|total)$/;

/** Unapplied / deferred / accrued / outstanding dollars (MER-438). */
const MONEY_ACCOUNTING = /(_|^)(unapplied|deferred|accrued|outstanding)/;

function isNonMoneyName(col) {
  return NON_MONEY_PREFIX.test(col) || NON_MONEY_SUFFIX.test(col);
}

/**
 * @param {string} column canonical column name, any case
 * @returns {boolean} true when the column holds dollars
 */
export function isMoneyColumnName(column) {
  const col = (column ?? "").toLowerCase();
  if (!col) return false;
  // The guard runs FIRST, ahead of every money rule. Put it later and
  // `is_below_disposal_cost`, `has_unapplied` and `is_invoice` all classify as
  // dollars off their tails before the boolean prefix is ever consulted.
  if (isNonMoneyName(col)) return false;
  if (AR_BUCKET.test(col)) return true;
  if (MONEY_TAIL.test(col)) return true;
  if (MONEY_ACCOUNTING.test(col)) return true;
  // MER-438: ar_current, ar_31_60, ar_over_120 and friends.
  if (/^ar_/.test(col)) return true;
  return MONEY_WORD.test(col);
}
